import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { cloneDeep, isEqual, set } from 'lodash'
import * as yup from 'yup'
import {
    getReducedWeighings,
    getWeighingMeanValue,
    isWeightCritical,
    getEmptyWeighing,
    packagerPrefix
} from './analysisUtils'
import { AddRowButton, RowRemoveButton, Asterisk } from '../common/components'
import LiveTooltip from 'live/components/ui/tooltip'
import { EmptyFields } from './analysisTypes'

const wheightValidation = yup
    .number()
    .min(0)
    .max(1000)

const integerWheightValidation = yup
    .number()
    .min(0)
    .max(1000)
    .integer()

interface IProps {
    generalInfo: GeneralInfo
    setGeneralInfo: React.Dispatch<React.SetStateAction<GeneralInfo>>
    analysisType: AnalysisTypes
    emptyFields: EmptyFields
    setEmptyFields: (emptyFields: EmptyFields) => void
}

type MeanStatus = 'goodMeanValue' | 'badMeanValue' | ''
export function WeighingInfo({
    generalInfo,
    setGeneralInfo,
    analysisType,
    emptyFields,
    setEmptyFields
}: IProps): JSX.Element {
    const [reducedWeighings, setReducedWeighings] = useState({ weights: [], critical: 0, delta: 0 })
    const [weighingInputAlert, setWeighingInputState] = useState('')

    useEffect(() => {
        if (emptyFields.fields.includes('weighings')) {
            setWeighingInputState('Alert')
        }
    }, [emptyFields, emptyFields.fields, emptyFields.trigger])

    const formatWeighingInputValue = (inputValue): string => {
        inputValue = inputValue.replace(' ', '')
        inputValue = inputValue.replace(/[^,0-9]/g, '')
        if (inputValue.split(',').length > 2) {
            const splitedInputValue = inputValue.split(',')
            inputValue = splitedInputValue[0] + ',' + splitedInputValue[1]
        }

        if (inputValue === '') return '0'
        else if (inputValue.match(/^0[0-9].*$/g)) return inputValue.substr(1)
        else return inputValue
    }

    const formatGrammageInputValue = (inputValue): string => {
        inputValue = inputValue.replaceAll(',', '')
        inputValue = inputValue.replaceAll('.', '')

        return inputValue
    }

    const isValidWeighingEntry = (inputValue): boolean => {
        const inputValueData = inputValue.split(',')
        if (inputValueData.length < 2) {
            return true
        }
        if (inputValueData[1].length > 2) {
            return false
        } else {
            return true
        }
    }

    const cleanWeighingInputStatus = (): void => {
        const localEmptyFields = emptyFields
        localEmptyFields.trigger = false
        localEmptyFields.fields = localEmptyFields.fields.filter(field => {
            return field !== 'weighings'
        })
        setEmptyFields(localEmptyFields)
        setWeighingInputState('')
    }

    function onChangeWeighings(
        weightType: 'grammage' | 'weight' | 'packager',
        index: number,
        inputValue: string
    ): void {
        cleanWeighingInputStatus()
        let newNumber

        weightType === 'weight'
            ? (newNumber = formatWeighingInputValue(inputValue))
            : (newNumber = Number(formatGrammageInputValue(inputValue)))

        if (
            weightType === 'weight' &&
            (!isValidWeighingEntry(newNumber) || !wheightValidation.isValidSync(newNumber.replace(',', '.')))
        ) {
            return
        }

        if (weightType === 'grammage' && !integerWheightValidation.isValidSync(newNumber)) {
            return
        }

        const packagerNumber = inputValue.split(packagerPrefix)?.[1]
        if (
            weightType === 'packager' &&
            (!inputValue.match(/^EA-\w{0,2}$/) || (packagerNumber && !wheightValidation.isValidSync(packagerNumber)))
        ) {
            return
        }

        const newConfigs = cloneDeep(generalInfo)

        let newValue
        switch (weightType) {
            case 'grammage':
                newValue = newNumber
                break
            case 'weight':
                newValue = newNumber.replace(',', '.')
                break
            default:
                newValue = inputValue
        }

        // when analysist if of type icc, all of the weighings packagers and grammage should be the same
        if (analysisType === 'icc' && weightType !== 'weight') {
            newConfigs.weighings = newConfigs.weighings.map(weighing => ({ ...weighing, [weightType]: newValue }))
        } else {
            set(newConfigs, `weighings[${index}].${weightType}`, newValue)
        }

        setGeneralInfo(newConfigs)
    }

    function onPackagerBlur(index: number): void {
        const newConfigs = cloneDeep(generalInfo)
        const packager = generalInfo.weighings[index].packager
        const splitPackager = packager.split('-')
        splitPackager[1] = splitPackager[1].padStart(2, '0')
        const newPackager = splitPackager.join('-')

        // when analysist if of type icc, all of the weighings packagers and grammage should be the same
        if (analysisType === 'icc') {
            newConfigs.weighings = newConfigs.weighings.map(weighing => ({
                ...weighing,
                packager: newPackager
            }))
        } else {
            set(newConfigs, `weighings[${index}].packager`, newPackager)
        }

        setGeneralInfo(newConfigs)
    }

    function onAddWeighing(event): void {
        event.preventDefault()
        const updatedInfo = cloneDeep(generalInfo)
        const firstWeighing = updatedInfo.weighings[0]
        const iccNewWeighing = {
            ...getEmptyWeighing(),
            packager: firstWeighing?.packager,
            grammage: firstWeighing?.grammage
        }

        // when analysist if of type icc, all of the weighings packagers and grammage should be the same
        const newWeighing = analysisType === 'icc' && firstWeighing ? iccNewWeighing : getEmptyWeighing()
        updatedInfo.weighings.push(newWeighing)
        setGeneralInfo(updatedInfo)
    }

    function onRemoveWeighing(event, index): void {
        event.preventDefault()
        const updatedInfo = cloneDeep(generalInfo)
        updatedInfo.weighings.splice(index, 1)
        setGeneralInfo(updatedInfo)
    }

    function getMeanStatus(): MeanStatus {
        if (!reducedWeighings.weights.length) return ''
        else if (reducedWeighings.critical) return 'badMeanValue'
        else return 'goodMeanValue'
    }

    function getMeanIcon(): JSX.Element {
        if (!reducedWeighings.weights.length) return <i className="fal fa-horizontal-rule" />
        else if (reducedWeighings.critical) return <i className="fas fa-times" />
        else return <i className="fas fa-check" />
    }

    function getWheighingScore(newReducedWeighings): number {
        if (!newReducedWeighings.weights.length) return undefined
        else if (newReducedWeighings.critical) return 4
        else return 0
    }

    function getWeighingClass(weight: number, grammage: number): string {
        if (isWeightCritical(weight, grammage)) {
            return 'critical'
        } else if (weight > grammage + grammage * 0.1) {
            return 'overweight'
        }

        return 'adequate'
    }

    function isOverweight(weight: number, grammage: number): boolean {
        return weight > grammage + grammage * 0.1
    }

    useEffect(() => {
        const newReducedWeighings = getReducedWeighings(generalInfo.weighings)
        if (!isEqual(newReducedWeighings, reducedWeighings)) {
            setReducedWeighings(newReducedWeighings)
            const updatedGeneralInfo = cloneDeep(generalInfo)
            updatedGeneralInfo.weighingAverage = getWeighingMeanValue(newReducedWeighings)
            updatedGeneralInfo.weighingScore = getWheighingScore(newReducedWeighings)
            setGeneralInfo(updatedGeneralInfo)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generalInfo, generalInfo.weighings, reducedWeighings, setGeneralInfo])

    return (
        <StyledWeighingInfo className={`weighingInfo`}>
            <h1 className={`weighingInfo--title${weighingInputAlert}`}>
                {i18n('weighings')}{' '}
                <LiveTooltip
                    position="top"
                    content={i18n(
                        'The first field refers to the weight of the package, the second field refers to a weighing for analysis and the third field is the wrapper number. All fields are mandatory.'
                    )}
                    hide={false}
                >
                    <span className="weighingInfo--title__info">
                        <i className="far fa-info-circle"></i>
                    </span>
                </LiveTooltip>
                <Asterisk />
            </h1>
            <ol className="weighingInfo--list">
                {generalInfo.weighings.map((weighing, index) => (
                    <li key={index} className="weighingInfo--list__item">
                        <span className="weighingInfo--list__item--number">{index + 1}.</span>
                        <div className="weighingInfo--list__item--grammage">
                            <input
                                type="text"
                                value={weighing.grammage}
                                onChange={event => onChangeWeighings('grammage', index, event.target.value)}
                                placeholder={i18n('grammage')}
                                min="0"
                                aria-label={'grammage'}
                            />
                        </div>
                        <div
                            className={`weighingInfo--list__item--weight ${getWeighingClass(
                                Number(weighing.weight),
                                Number(weighing.grammage)
                            )}`}
                        >
                            {!weighing.weight || !weighing.grammage ? null : isOverweight(
                                  weighing.weight,
                                  weighing.grammage
                              ) ? (
                                <LiveTooltip
                                    position="top"
                                    content={i18n(
                                        'This weight is 10% greater than the grammage. Check the inserted data!'
                                    )}
                                    hide={false}
                                >
                                    <i className={`fas fa-exclamation-triangle`} />
                                </LiveTooltip>
                            ) : (
                                <i
                                    className={`fas fa-${
                                        isWeightCritical(Number(weighing.weight), Number(weighing.grammage))
                                            ? 'times'
                                            : 'check'
                                    }`}
                                />
                            )}
                            <input
                                type="text"
                                value={weighing.weight.toString().replace('.', ',')}
                                onChange={event => {
                                    onChangeWeighings('weight', index, event.target.value)
                                }}
                                placeholder={i18n('weight') + ' 0,00'}
                                disabled={!weighing.grammage}
                                min="0"
                                max="1000"
                                aria-label={'weight'}
                            />
                        </div>
                        <div className="weighingInfo--list__item--packager">
                            <input
                                type="text"
                                value={weighing.packager}
                                onChange={event => onChangeWeighings('packager', index, event.target.value)}
                                placeholder={i18n('packager')}
                                min="0"
                                onBlur={() => onPackagerBlur(index)}
                                aria-label={'packager'}
                            />
                        </div>
                        <RowRemoveButton
                            className="weighingInfo--list__item--remove"
                            onMouseDown={event => onRemoveWeighing(event, index)}
                        >
                            <i className="far fa-trash-alt" />
                        </RowRemoveButton>
                    </li>
                ))}
            </ol>
            <AddRowButton className="weighingInfo--addButton" onClick={event => onAddWeighing(event)}>
                <i className="fas fa-plus" /> {i18n('add weighing')}
            </AddRowButton>
            {analysisType === 'icc' ? (
                <div className="weighingInfo--average">
                    <span className="weighingInfo--average__title">{i18n('final average')}</span>
                    <span className={`weighingInfo--average__input ${getMeanStatus()}`}>
                        {getMeanIcon()}
                        {generalInfo.weighingAverage}
                    </span>
                </div>
            ) : null}
        </StyledWeighingInfo>
    )
}

const StyledWeighingInfo = styled.div`
    --border: 1px solid ${({ theme }) => theme['gray-400']};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    align-self: start;
    border: var(--border);
    grid-area: weighings;

    flex-grow: 1;
    border-radius: 5px;

    .weighingInfo {
        &--titleAlert {
            background-color: #fdd0cf;
        }
        &--titleAlert,
        &--title {
            width: 100%;
            padding: 12px 24px;
            text-transform: uppercase;
            font-family: ${({ theme }) => theme['font-semibold']};
            color: ${({ theme }) => theme['gray-800']};
            border-bottom: var(--border);
            font-size: 16px;
            line-height: 16px;
            &__info {
                color: #37a3ef;
                font-size: 14px;
            }
        }
        &--average {
            display: flex;
            &__title,
            &__input {
                padding: 10px 24px;
            }
            &__title {
                font-family: ${({ theme }) => theme['font-semibold']};
                font-size: 12px;
                text-transform: uppercase;
                border-right: var(--border);
                background: ${({ theme }) => theme['gray-100']};
                flex-grow: 1;
            }
            &__input {
                background: ${({ theme }) => theme['gray-200']};
                display: flex;
                align-items: center;
                width: 30%;
                i {
                    margin-right: 8px;
                }
                &.goodMeanValue {
                    color: ${({ theme }) => theme['green-700']};
                    background: ${({ theme }) => theme['green-200']};
                }
                &.badMeanValue {
                    background: ${({ theme }) => theme['red-300']};
                    color: ${({ theme }) => theme['red-700']};
                }
            }
        }
        &--list {
            margin: 0;
            &__item {
                border: 1px solid transparent;
                border-bottom: var(--border);
                display: flex;
                align-items: stretch;

                &--number {
                    font-size: 14px;
                    margin-left: 15px;
                    font-family: ${({ theme }) => theme['font-semibold']};
                    display: flex;
                    align-items: center;
                }
                &:focus-within {
                    border-color: ${({ theme }) => theme['blue-500']};
                }
                &::marker {
                    font-family: ${({ theme }) => theme['font-semibold']};
                    font-size: 13px;
                    color: ${({ theme }) => theme['gray-700']};
                }
                &--grammage,
                &--weight,
                &--packager {
                    display: flex;
                    align-items: center;
                    min-width: 90px;
                    flex-grow: 1;
                    input {
                        padding: 8px 12px 8px 12px;
                        border: none;
                        box-shadow: none;
                        flex-grow: 1;
                        height: 100%;
                    }
                }
                &--weight {
                    border-left: var(--border);
                    border-right: var(--border);
                    font-family: ${({ theme }) => theme['font-semibold']};
                    position: relative;
                    i {
                        position: absolute;
                        top: 50%;
                        left: 12px;
                        transform: translateY(-50%);
                    }
                    input {
                        padding-left: 30px;
                    }
                    &.critical {
                        color: var(--red-700);
                    }
                    &.adequate {
                        color: var(--green-700);
                    }
                    &.overweight {
                        color: var(--yellow-700);
                    }
                }
            }
        }
        &--addButton {
            border-bottom: var(--border);
        }
    }
`
